/* eslint-disable no-undef */
import get from 'lodash/get';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import classNames from 'clsx';
import { hasPairAuthFlowParam } from 'src/common/utils/auth/pairAuthFlowHelpers';
import RecurlyField from '../../components/shared/form/RecurlyField';

import { selectLocationOption } from '../../actions/location';
import { updateLocationPaymentInformation } from '../../actions/subscription';

import standardizeRecurlyError from '../../utils/subscription/standardizeRecurlyError';

import recurlyFieldNames from '../../constants/subscription/recurlyFieldNames';
import paymentFlows from '../../constants/payment/paymentFlows';
import {
  SUBSCRIBE_PAYMENT_FORM_CARD_ERROR,
  SUBSCRIBE_PAYMENT_FORM_CARD_NUMBER_ERROR,
  SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_ERROR,
  SUBSCRIBE_PAYMENT_FORM_CARD_CVV_ERROR,
} from '../../constants/localizations/subscription';

import { XSMALL_MINUS_1 } from '../../styles/breakpoints';
import css from '../../components/payment/payment.module.scss';
import recurlyCss from '../../components/shared/form/recurlyField.module.scss';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';

/**
 * A component that wraps the CreditCard component and provides needed
 * state and functionality for recurly form elements, validation, and
 * location state
 * @param FormComponent {ReactComponent}
 * @returns {ReactComponent}
 */
export default function withCreditCardHelper(FormComponent) {
  class WithCreditCardHelper extends Component {
    static propTypes = {
      recurly: PropTypes.object.isRequired,
      breakpoint: PropTypes.number.isRequired,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      addressOne: PropTypes.string,
      addressTwo: PropTypes.string,
      city: PropTypes.string,
      postalCode: PropTypes.string,
      state: PropTypes.string,
      country: PropTypes.string,
      isFormDisabled: PropTypes.bool.isRequired,
      currentPaymentFlow: PropTypes.string.isRequired,
      enableManualSelection: PropTypes.bool,
    };

    static contextType = LocationAndLocalizationContext;

    constructor(props) {
      super(props);
      this.onChange = this.onChange.bind(this);
    }

    state = {
      fieldValidation: {},
    };

    componentDidMount() {
      this.props.recurly.on('change', this.onChange);
    }

    componentWillUnmount() {
      // avoid memory leaks
      this.props.recurly.off('change', this.onChange);
    }

    /**
     * onChange handler for the Recurly.js field 'change' event. Sets the fields validation object
     * on the component state so that it can be passed to the necessary Recurly fields.
     * @param validation {object} See withRecurlyFieldValidation for description of object structure
     */
    onChange(validation) {
      this.setState({ fieldValidation: validation });
    }

    /**
     * Gets the nested validation object for a given Recurly.js field, designated by fieldName.
     * @param fieldName {string} Reecurly.js field name
     * @returns {object}
     */
    getFieldValidation(fieldName) {
      return get(this.state.fieldValidation, `fields.${fieldName}`, {});
    }

    /**
     * A method to combine the validations for month and year Recurly.js fields so that we
     * can treat these fields a single expiration field in the UI.
     * @returns {object}
     */
    getExpirationDateValidation() {
      const { fields } = this.state.fieldValidation;
      const month = get(fields, 'month', {});
      const year = get(fields, 'year', {});

      // We want to give the user a chance to input the entire date before we try
      // to run validation on it.
      //
      // So:
      //
      // 1) If they are moving from the MONTH field to the YEAR field, and the
      //    YEAR field IS EMPTY and the MONTH field is NOT EMPTY, then assume that
      //    the field is valid.
      //
      // 2) If they are moving from the YEAR field to the MONTH field and the
      //    MONTH field IS EMPTY and the YEAR field is NOT EMPTY, then assume that
      //    the field is valid.
      //
      // 3) Otherwise, the expiration field is only valid if both the month and year
      //    fields are valid.
      //
      const movingFromMonthToYear = year.focus && year.empty && !month.empty;
      const movingFromYearToMonth = month.focus && month.empty && !year.empty;
      const expirationIsValid = month.valid && year.valid;
      return {
        valid:
          movingFromMonthToYear || movingFromYearToMonth || expirationIsValid,
        empty: month.empty && year.empty,
        length: month.length || year.length,
        focus: month.focus || year.focus,
      };
    }

    /**
     * On screen sizes that are LARGER than 414, we want show the single Recurly.js
     * card field. This function returns the structure for that single card field.
     * @returns {object}
     */
    getSingleCardField(isPairingFlow) {
      const { card } = recurlyFieldNames;

      return (
        <div className={classNames('row', css.paymentFieldSection)}>
          <div data-testid="cardNumberContainer" className="col-xs-12">
            <RecurlyField
              data-testid="recurlyCardField"
              isPairingFlow={isPairingFlow}
              recurlyValidation={this.getFieldValidation(card)}
              fieldName={card}
              customError={this.context.getLocalizedText(
                SUBSCRIBE_PAYMENT_FORM_CARD_ERROR,
              )}
              disableField={this.shouldDisableForm()}
              required
            >
              <div
                data-recurly={card}
                data-testid={`recurly-${card}-field-iframe-container`}
              />
            </RecurlyField>
          </div>
        </div>
      );
    }

    /**
     * On screen sizes that are SMALLER than 414, we want to show separate card fields for
     * number, expiration and CVV.
     * @returns {object}
     */
    getMultipleCardFields(isPairingFlow) {
      const { getLocalizedText } = this.context;
      const { number, expiration, month, year, cvv } = recurlyFieldNames;

      return (
        <div>
          <div className={classNames('row', css.paymentFieldSection)}>
            <div data-testid="cardNumberContainer" className="col-xs-12">
              <RecurlyField
                data-testid="recurlyCardField"
                isPairingFlow={isPairingFlow}
                recurlyValidation={this.getFieldValidation(number)}
                fieldName={number}
                customError={getLocalizedText(
                  SUBSCRIBE_PAYMENT_FORM_CARD_NUMBER_ERROR,
                )}
                disableField={this.shouldDisableForm()}
                required
              >
                <div
                  data-recurly={number}
                  data-testid={`recurly-${number}-field-iframe-container`}
                />
              </RecurlyField>
            </div>
          </div>
          <div className={classNames('row', css.paymentFieldSection)}>
            <div
              data-testid="recurlyCardExpirationFieldContainer"
              className="col-xs-7"
            >
              <RecurlyField
                data-testid="recurlyCardExpirationField"
                isPairingFlow={isPairingFlow}
                recurlyValidation={this.getExpirationDateValidation()}
                fieldName={expiration}
                customError={getLocalizedText(
                  SUBSCRIBE_PAYMENT_FORM_CARD_EXPIRATION_ERROR,
                )}
                disableField={this.shouldDisableForm()}
                required
              >
                <div className="row">
                  <div className="col-xs-5">
                    <div
                      data-recurly={month}
                      data-testid={`recurly-${month}-field-iframe-container`}
                    />
                  </div>
                  <div className="col-xs-1">
                    <span className={recurlyCss.slash}>/</span>
                  </div>
                  <div className="col-xs-6">
                    <div
                      data-recurly={year}
                      data-testid={`recurly-${year}-field-iframe-container`}
                    />
                  </div>
                </div>
              </RecurlyField>
            </div>
            <div data-testid="cardCvvFieldContainer" className="col-xs-5">
              <RecurlyField
                data-testid="recurlyCardCvvField"
                isPairingFlow={isPairingFlow}
                recurlyValidation={this.getFieldValidation(cvv)}
                fieldName={cvv}
                customError={getLocalizedText(
                  SUBSCRIBE_PAYMENT_FORM_CARD_CVV_ERROR,
                )}
                disableField={this.shouldDisableForm()}
                required
              >
                <div
                  data-recurly={cvv}
                  data-testid={`recurly-${cvv}-field-iframe-container`}
                />
              </RecurlyField>
            </div>
          </div>
        </div>
      );
    }

    /**
     * Function that allows us to render either a single card field, or multiple ones depending
     * on the screen size designated by the breakpoint.
     * @returns {Object}
     */
    getRecurlyCardFields() {
      const { location } = this.context;

      const isPairingFlow = hasPairAuthFlowParam(location?.query);

      if (this.props.breakpoint >= XSMALL_MINUS_1) {
        return this.getSingleCardField(isPairingFlow);
      }
      return this.getMultipleCardFields(isPairingFlow);
    }

    /**
     * Method to return the required Recurly.js hidden card fields that are used by Recurly.js to
     * generate a token for the billing information in generateRecurlyToken.
     * @returns {object}
     */
    getRecurlyHiddenFields() {
      return (
        <div>
          <input
            type="hidden"
            name="recurly-firstName"
            data-recurly="first_name"
            ref={(input) => {
              this.firstNameInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-lastName"
            data-recurly="last_name"
            ref={(input) => {
              this.lastNameInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-address1"
            data-recurly="address1"
            ref={(input) => {
              this.addressOneInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-address2"
            data-recurly="address2"
            ref={(input) => {
              this.addressTwoInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-city"
            data-recurly="city"
            ref={(input) => {
              this.cityInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-postalCode"
            data-recurly="postal_code"
            ref={(input) => {
              this.postalCodeInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-state"
            data-recurly="state"
            ref={(input) => {
              this.stateInput = input;
            }}
          />
          <input
            type="hidden"
            name="recurly-country"
            data-recurly="country"
            ref={(input) => {
              this.countryInput = input;
            }}
          />
          <input type="hidden" name="recurly-token" data-recurly="token" />
        </div>
      );
    }

    /**
     * Method that is passed to the PaymentForm so that the form element can be set
     * on this component and passed to the Recurly.js library's token method.
     * @param element {HTMLFormElement}
     */
    setFormInstance = (element) => {
      this.form = element;
    };

    /**
     * A function that is responsible for generating recurly tokens. The recurly.token call
     * must be passed the form DOM element that contains the Hosted Fields that contains
     * the credit card billing information. This function is also responsible for updating the
     * hidden inputs, that are injected in the render function below, with their associated
     * values from the store (firstName, lastName, postalCode, state, country).
     * @returns {Promise} - resolves with a recurly token, or rejects with the recurly error.
     */
    generateRecurlyToken = () => {
      const {
        firstName,
        lastName,
        addressOne,
        addressTwo,
        city,
        postalCode,
        state,
        country,
        recurly,
      } = this.props;
      this.firstNameInput.value = firstName;
      this.lastNameInput.value = lastName;
      this.addressOneInput.value = addressOne;
      this.addressTwoInput.value = addressTwo || '';
      this.cityInput.value = city;
      this.postalCodeInput.value = postalCode;
      this.stateInput.value = state;
      this.countryInput.value = country;
      return new Promise((resolve, reject) => {
        recurly.token(this.form, (error, token) => {
          if (error) {
            reject(standardizeRecurlyError(error));
          } else {
            resolve(token && token.id);
          }
        });
      });
    };

    shouldDisableForm() {
      return (
        this.props.isFormDisabled ||
        this.props.currentPaymentFlow !== paymentFlows.creditCard
      );
    }

    /**
     * This render function adds hidden inputs that contain the required data-recurly
     * attributes that the library needs when generating the token above.
     * @returns {ReactComponent}
     */
    render() {
      return (
        <FormComponent
          {...this.props}
          setFormInstance={this.setFormInstance}
          generateRecurlyToken={this.generateRecurlyToken}
          recurlyCardFields={this.getRecurlyCardFields()}
          recurlyHiddenFields={this.getRecurlyHiddenFields()}
          isFormDisabled={this.shouldDisableForm()}
        />
      );
    }
  }

  function mapStateToProps(state, currentProps) {
    const values = get(state, `form.${currentProps.formId}.values`, {});
    const paymentInfo = get(state, 'subscription.paymentInfo', {});
    const countriesByZip = paymentInfo.options
      ? paymentInfo.options.map((l, i) => ({
          name: `${l.stateName}, ${l.countryName}`,
          value: i,
        }))
      : null;

    return {
      country: paymentInfo.country,
      state: paymentInfo.state,
      firstName: values.firstName,
      lastName: values.lastName,
      addressOne: values.addressOne,
      addressTwo: values.addressTwo,
      city: values.city,
      postalCode: values.postalCode,
      locationOptions: countriesByZip,
      enableManualSelection: paymentInfo.enableManualSelection,
    };
  }

  function mapDispatchToProps(dispatch) {
    const actions = {
      selectLocationOption,
      updateLocationPaymentInformation,
    };

    return {
      actions: bindActionCreators(actions, dispatch),
    };
  }

  return connect(mapStateToProps, mapDispatchToProps)(WithCreditCardHelper);
}
